/**
 * svgGhostStroke — attach/remove a stroke-only ghost path sibling for
 * compartment hover/select visual feedback on SVG floor plans.
 *
 * Problem:
 *   Stroke on `path.compartment` is invisible: the own fill covers the inner
 *   half, and SVG siblings rendered after it cover the outer half. Adding
 *   `paint-order: stroke fill` fixes the own-fill issue but not sibling overlap.
 *
 * Solution:
 *   Create a sibling `<path fill="none" stroke="var(…)">` with the same `d`,
 *   appended last to `<g class="compartments">`. It renders after all siblings,
 *   so its stroke is never covered.
 *
 * Two ghosts are managed per renderer: one for hover, one for selected state.
 * Each ghost is tracked via a React ref (`{ current: SVGPathElement | null }`).
 *
 * Ghost paths are SVG namespace elements — created with `createElementNS`.
 * `pointer-events: none` ensures they don't interfere with mouse handling.
 */

const SVG_NS = 'http://www.w3.org/2000/svg'

/**
 * Attaches a stroke-only ghost path to visualise a compartment path's stroke
 * over all SVG siblings.
 *
 * Copies the `d` attribute from `sourcePath`, sets `fill="none"`, sets
 * `stroke` to `strokeColor`, disables pointer events, and appends the
 * ghost as the last child of `sourcePath.parentNode`.
 *
 * If `ghostRef.current` already holds a ghost from a previous call, it is
 * removed first to avoid accumulation.
 *
 * @param {SVGPathElement}                        sourcePath   — the compartment path to outline
 * @param {string}                                strokeColor  — full CSS color value, e.g. 'var(--bim-color-product-outline, var(--bim-color-hero-outlines))'
 * @param {string|number}                                strokeWidth  — width of the stroke
 * @param {{ current: SVGPathElement | null }}    ghostRef     — ref tracking the live ghost element
 */
export function attachStrokeGhost(sourcePath, strokeColor, strokeWidth = "12", ghostRef) {
  removeStrokeGhost(ghostRef)

  const ghost = document.createElementNS(SVG_NS, 'path')
  ghost.setAttribute('d', sourcePath.getAttribute('d') ?? '')
  ghost.setAttribute('fill', 'none')
  ghost.setAttribute('stroke', strokeColor)
  ghost.setAttribute('stroke-width', strokeWidth.toString())
  ghost.style.pointerEvents = 'none'

  sourcePath.parentNode.appendChild(ghost)
  ghostRef.current = ghost
}

/**
 * Removes the stroke ghost path from the DOM and clears the ref.
 * Safe to call when `ghostRef.current` is already null.
 *
 * @param {{ current: SVGPathElement | null }} ghostRef
 */
export function removeStrokeGhost(ghostRef) {
  if (ghostRef.current) {
    ghostRef.current.remove()
    ghostRef.current = null
  }
}
